import { measuredGaze, objectCaptionFont } from "../player/overlayVisibility";
import { placeLabel, type LabelRect } from "../player/labelLayout";
import { AnnotationHistory } from "../player/annotationHistory";
import { adjacentChapter, isMac } from "../player/annotationEditing";
import { currentActivity, isActivityLabel } from "../player/activityLabels";
import { FrameLabels } from "../components/FrameLabels";
import { GazeOffsetSettings, useGazeOffset } from "../components/GazeOffsetSettings";
import { ObjectVisibilitySettings, useObjectVisibility } from "../components/ObjectVisibilitySettings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import type {
  AnalysisPlugin, Annotation, GazeSample, ObjectFrame, FrameIndexResponse, HandFrames, Ontology,
  SessionDetail, SyncInfo, TimeMapping, WaveformData,
} from "../api/types";
import { useApp } from "../state/AppContext";
import { SplitPane } from "../components/SplitPane";
import { Timeline } from "../components/Timeline";
import { OverlayBar, VideoPanel, type ViewTransform } from "../components/VideoPanel";
import { AnnotationInspector } from "../components/AnnotationInspector";
import { AnalysisPanel } from "../components/AnalysisPanel";
import { CaptureOverview } from "../components/CaptureOverview";
import { DepthLayer } from "../components/DepthLayer";
import { ExportPanel } from "../components/ExportPanel";
import { SyncInspector } from "../components/SyncInspector";
import { drawArms, drawHands } from "../player/overlays";
import { Badge, EmptyState, KeyValue, Spinner, StateDot } from "../components/ui";
import { useSyncPlayer, type MediaSource } from "../player/useSyncPlayer";
import { NS, formatBytes, formatTimecode, nearestIndex } from "../player/time";

type Tab = "overview" | "annotations" | "analysis" | "sync" | "export" | "streams" | "shortcuts";



export function ReviewWorkspace({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { notify, info, jobs } = useApp();
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [frames, setFrames] = useState<Record<string, FrameIndexResponse>>({});
  const [waveform, setWaveform] = useState<WaveformData | null>(null);
  const [sync, setSync] = useState<SyncInfo | null>(null);
  const [mappings, setMappings] = useState<Record<string, TimeMapping>>({});
  const [syncSeries, setSyncSeries] = useState<{ left_ns: number[]; delta_ms: number[]; valid: boolean[] } | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [ontology, setOntology] = useState<Ontology | null>(null);
  const [selectedAnn, setSelectedAnn] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useState<string | null>(null);
  const [linkViews, setLinkViews] = useState(true);
  const [leftView, setLeftView] = useState<ViewTransform>({ scale: 1, x: 0, y: 0 });
  const [rightView, setRightView] = useState<ViewTransform>({ scale: 1, x: 0, y: 0 });
  const [plugins, setPlugins] = useState<AnalysisPlugin[]>([]);
  const [handFrames, setHandFrames] = useState<HandFrames | null>(null);
  const [denseDepth, setDenseDepth] = useState<Awaited<ReturnType<typeof api.denseDepthFrames>> | null>(null);
  const [objectFrames, setObjectFrames] = useState<ObjectFrame[]>([]);
  const objectVisibility = useObjectVisibility(sessionId, objectFrames);
  const [faceFrames, setFaceFrames] = useState<Awaited<ReturnType<typeof api.faceFrames>>["frames"]>([]);
  const [gazeSamples, setGazeSamples] = useState<GazeSample[]>([]);
  const gazeOffset = useGazeOffset(sessionId);
  const [overlays, setOverlays] = useState<Record<string, boolean>>({"analysis.hands":true,"analysis.faces":true,"analysis.objects":true,"analysis.dense_depth":true,"stream.gaze":true});
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;
  const [depthOpacity, setDepthOpacity] = useState(0.65);
  const readyPreviews = useRef<Record<string, string | null>>({});
  const [showRejectedArms, setShowRejectedArms] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [markIn, setMarkIn] = useState<number | null>(null);
  const [markOut, setMarkOut] = useState<number | null>(null);
  const [, setHistoryVersion] = useState(0);
  const history = useMemo(() => new AnnotationHistory(() => setHistoryVersion(v => v+1)), [sessionId]);
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;
  const annotationRequest = useRef(0);
  const analysisRequest = useRef(0);

  /* ---------------- loading ---------------- */
  const loadSync = useCallback(async () => {
    const s = await api.sync(sessionId);
    setSync(s.sync);
    setMappings(s.mappings);
    setSyncSeries(s.series);
  }, [sessionId]);

  const loadAnnotations = useCallback(async () => {
    const request = ++annotationRequest.current;
    const r = await api.annotations(sessionId);
    if (request === annotationRequest.current) {
      annotationsRef.current = r.annotations;
      setAnnotations(r.annotations);
    }
  }, [sessionId]);

  /** Plugin availability plus whatever each has already produced. */
  const loadAnalysis = useCallback(async () => {
    const request = ++analysisRequest.current;
    const list = await api.analysis(sessionId).catch(() => null);
    if (!list || request !== analysisRequest.current) return;
    setPlugins(list.plugins);
    const available = (id: string) => list.plugins.some(p => p.id === id && p.has_result);
    const apply = <T,>(value: T, setter: (value: T) => void) => {
      if (request === analysisRequest.current) setter(value);
    };
    // Publish each layer independently: a large hand payload must not delay depth.
    await Promise.all([
      (available("objects.yolox") ? api.objectFrames(sessionId) : Promise.resolve(null)).then(r => apply(r?.frames ?? [], setObjectFrames)).catch(() => undefined),
      (available("faces.mediapipe") ? api.faceFrames(sessionId) : Promise.resolve(null)).then(r => apply(r?.frames ?? [], setFaceFrames)).catch(() => undefined),
      api.gaze(sessionId).then(r => apply(r.samples.filter(measuredGaze), setGazeSamples)).catch(() => undefined),
      (available("hands.mediapipe") ? api.handFrames(sessionId) : Promise.resolve(null)).then(r => apply(r, setHandFrames)).catch(() => undefined),
      (available("depth.relative") ? api.denseDepthFrames(sessionId) : Promise.resolve(null)).then(r => apply(r, setDenseDepth)).catch(() => undefined),
    ]);
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setGazeSamples([]); setPlugins([]); setObjectFrames([]); setFaceFrames([]); setHandFrames(null); setDenseDepth(null); setDetail(null);
    void loadAnalysis();
    (async () => {
      try {
        const d = await api.session(sessionId);
        if (cancelled) return;
        setDetail(d);
        const [left, right] = await Promise.all([
          api.frames(sessionId, "left").catch(() => null),
          api.frames(sessionId, "right").catch(() => null),
        ]);
        if (cancelled) return;
        const map: Record<string, FrameIndexResponse> = {};
        if (left) map["camera.left"] = left;
        if (right) map["camera.right"] = right;
        setFrames(map);
        setWaveform(await api.waveform(sessionId).catch(() => null));
        await loadSync().catch(() => undefined);
        await loadAnnotations();

        setOntology(await api.ontology().catch(() => null));
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; analysisRequest.current++; };
  }, [sessionId, loadSync, loadAnnotations, loadAnalysis]);

  // An analysis job finishing changes what there is to draw; reload when one does.
  const finishedAnalysis = jobs
    .filter((j) => ["analysis", "local_pipeline", "upload_ingest", "calibrate_checkerboard"].includes(j.kind) && j.session_id === sessionId && j.state === "succeeded")
    .map((j) => j.id)
    .join(",");
  // Update completed layers between stages, not on every progress/frame event.
  const pipelineStage = jobs
    .filter(j => ["local_pipeline", "upload_ingest"].includes(j.kind) && j.session_id === sessionId && j.state === "running")
    .map(j => `${j.id}:${(j.message ?? "").split(" · ")[0]}`).join(",");
  useEffect(() => {
    if (pipelineStage) { void loadAnalysis(); void loadAnnotations(); }
  }, [pipelineStage, loadAnalysis, loadAnnotations]);
  useEffect(() => {
    if (finishedAnalysis) {
      void loadAnalysis(); void loadAnnotations(); void loadSync();
      void api.session(sessionId).then(setDetail);
      void Promise.all(["left", "right"].map(role => api.frames(sessionId, role))).then(([left,right]) => setFrames({"camera.left":left,"camera.right":right}));
    }
  }, [finishedAnalysis, loadAnalysis, loadAnnotations, loadSync, sessionId]);

  /* ---------------- player ---------------- */
  const relativeDepth = !!overlays["analysis.dense_depth"] && denseDepth != null;
  const depthVideo = relativeDepth ? {...denseDepth.video,near_m:1,far_m:2} : null;

  const sources: MediaSource[] = useMemo(() => {
    if (!detail) return [];
    const out: MediaSource[] = [];
    for (const s of detail.streams) {
      const rendition = s.renditions.find((r) => r.url);
      if (!rendition?.url || (s.kind !== "video" && s.kind !== "audio")) continue;
      out.push({
        key: s.stream_id,
        url: rendition.url,
        playbackOffsetNs: rendition.playback_offset_ns,
        mapping: s.mapping,
        kind: s.kind,
        stillPreview: !!s.frame_preview_url,
      });
    }
    // The depth overlay is a video like any other: it goes through the same
    // master clock rather than being stepped by hand.
    if (depthVideo?.url && relativeDepth) {
      out.push({
        key: "depth.overlay",
        url: depthVideo.url,
        playbackOffsetNs: depthVideo.playback_offset_ns ?? 0,
        kind: "video",
        stillPreview: !!denseDepth?.frame_url,
      });
    }
    return out;
  }, [detail, depthVideo?.url, depthVideo?.playback_offset_ns, relativeDepth, denseDepth?.frame_url]);

  const rangeNs: [number, number] = useMemo(() => {
    const starts: number[] = [];
    const ends: number[] = [];
    for (const f of Object.values(frames)) {
      if (f.session_ns.length) {
        starts.push(f.session_ns[0]);
        ends.push(f.session_ns[f.session_ns.length - 1]);
      }
    }
    if (waveform) {
      starts.push(waveform.start_ns);
      ends.push(waveform.start_ns + (waveform.bins / waveform.bins_per_second) * NS);
    }
    if (!starts.length) return [0, 10 * NS];
    return [Math.min(...starts), Math.max(...ends)];
  }, [frames, waveform]);

  const frameTimes = useMemo(() => {
    const out: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(frames)) out[k] = v.session_ns;
    return out;
  }, [frames]);

  const facesByEye = useMemo(() => ({left:faceFrames.filter(f=>f.eye==="left"),right:faceFrames.filter(f=>f.eye==="right")}),[faceFrames]);
  const faceTimes = useMemo(() => ({left:facesByEye.left.map(f=>f.session_ns),right:facesByEye.right.map(f=>f.session_ns)}),[facesByEye]);
  const objectTimes = useMemo(() => objectFrames.map(f => f.session_ns), [objectFrames]);
  const gazeTimes = useMemo(() => gazeSamples.map(g => g.session_ns), [gazeSamples]);
  const player = useSyncPlayer({ sources, clockKey: "camera.left", frameTimes, rangeNs });
  const [viewNs, setViewNs] = useState<[number, number]>(rangeNs);
  useEffect(() => setViewNs(rangeNs), [rangeNs]);

  // Open on the first frame both eyes have, not on the session's zero. Audio
  // starts seconds before video on these recordings, so the default playhead
  // sat before any picture existed — and before any analysed sample.
  const openedAt = useRef<string | null>(null);
  useEffect(() => {
    const left = frames["camera.left"]?.session_ns;
    const right = frames["camera.right"]?.session_ns;
    if (!left?.length || openedAt.current === sessionId) return;
    openedAt.current = sessionId;
    player.seek(Math.max(left[0], right?.[0] ?? left[0]));
  }, [frames, sessionId, player]);

  /* ---------------- annotation actions ---------------- */
  const createAnnotation = useCallback(async (body: Partial<Annotation>) => {
    let created: Annotation | undefined;
    await history.perform(async () => {
      created = (await api.createAnnotation(sessionId, body)).annotation;
      const id = created.id;
      await loadAnnotations(); setSelectedAnn(id);
      return {label:"create",
        undo:async () => {await api.deleteAnnotation(sessionId,id);await loadAnnotations();setSelectedAnn(null);},
        redo:async () => {await api.restoreAnnotation(sessionId,id);await loadAnnotations();setSelectedAnn(id);}};
    });
    return created!;
  }, [sessionId,loadAnnotations,history]);

  const defaultKind = (geometry: "point" | "interval") =>
    ontology?.kinds.find((k) => k.geometry === geometry)?.id ?? (geometry === "point" ? "event" : "action");

  const addPoint = useCallback(() => {
    void createAnnotation({ kind: defaultKind("point"), label: "", start_ns: player.masterNs, source: "manual" })
      .then(() => notify("Point annotation added"))
      .catch((e) => notify(e.message, "error"));
  }, [createAnnotation, player.masterNs, notify, ontology]);

  const addInterval = useCallback(
    (startNs?: number, endNs?: number) => {
      const a = startNs ?? markIn ?? player.masterNs;
      const b = endNs ?? markOut ?? player.masterNs + NS;
      if (b <= a) { notify("The interval's end is not after its start", "error"); return; }
      void createAnnotation({ kind: defaultKind("interval"), label: "", start_ns: a, end_ns: b, source: "manual", payload:{type:"activity",level:"activity"} })
        .then(() => { setMarkIn(null); setMarkOut(null); notify("Interval added"); })
        .catch((e) => notify(e.message, "error"));
    },
    [createAnnotation, markIn, markOut, player.masterNs, notify, ontology],
  );

  const updateAnnotation = useCallback(async (id: string, incoming: Partial<Annotation>) => {
    await history.perform(async () => {
      const before = annotationsRef.current.find(a => a.id === id);
      if (!before) return null;
      const patch = {...incoming};
      if (Object.entries(patch).every(([k,v]) => JSON.stringify((before as any)[k]) === JSON.stringify(v))) return null;
      if (before.source === "model" && !("review_status" in patch)) patch.review_status = "edited";
      const revert: Partial<Annotation> = {};
      for (const k of Object.keys(patch) as (keyof Annotation)[]) (revert as any)[k] = (before as any)[k];
      const apply = async (value: Partial<Annotation>) => {
        ++annotationRequest.current;
        await api.updateAnnotation(sessionId,id,value);
        await loadAnnotations();
      };
      await apply(patch);
      return {label:"edit",undo:() => apply(revert),redo:() => apply(patch)};
    });
  }, [sessionId,loadAnnotations,history]);

  const deleteAnnotation = useCallback(async (id: string) => {
    await history.perform(async () => {
      await api.deleteAnnotation(sessionId,id); await loadAnnotations();setSelectedAnn(null);
      return {label:"delete",
        undo:async () => {await api.restoreAnnotation(sessionId,id);await loadAnnotations();setSelectedAnn(id);},
        redo:async () => {await api.deleteAnnotation(sessionId,id);await loadAnnotations();setSelectedAnn(null);}};
    });
  }, [sessionId,loadAnnotations,history]);
  const undo = useCallback(() => history.undo().catch(e => notify(e.message,"error")), [history,notify]);
  const redo = useCallback(() => history.redo().catch(e => notify(e.message,"error")), [history,notify]);
  const jumpChapter = useCallback((direction: -1 | 1) => {
    const chapter = adjacentChapter(annotations,player.masterNs,direction);
    if (chapter) {player.pause();player.seek(chapter.start_ns);setSelectedAnn(chapter.id);}
  }, [annotations,player]);

  /* ---------------- keyboard ---------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const editing = target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
      const meta = e.metaKey || e.ctrlKey;
      const historyKey = meta && (e.key.toLowerCase() === "z" || (!isMac() && e.key.toLowerCase() === "y"));
      // A clean annotation field uses document undo; unsaved typing uses native undo.
      if (editing && !(historyKey && target.dataset.annotationInput && target.dataset.dirty === "false")) return;
      if (historyKey) {
        e.preventDefault();
        void (e.shiftKey || e.key.toLowerCase() === "y" ? redo() : undo());
        return;
      }
      if (meta && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();jumpChapter(e.key === "ArrowLeft" ? -1 : 1);return;
      }
      if (meta || e.altKey) return;
      switch (e.key.toLowerCase()) {
        case " ": e.preventDefault(); player.toggle(); break;
        case "k": player.pause(); player.setRate(1); break;
        case "l": player.setRate(player.playing ? Math.min(4, player.rate * 2) : 1); player.play(); break;
        case "j": player.setRate(1); player.seek(player.masterNs - NS); break;
        case "arrowleft": e.preventDefault(); player.stepFrames(e.shiftKey ? -10 : -1); break;
        case "arrowright": e.preventDefault(); player.stepFrames(e.shiftKey ? 10 : 1); break;
        case "home": player.seek(rangeNs[0]); break;
        case "end": player.seek(rangeNs[1]); break;
        case "i": setMarkIn(player.masterNs); break;
        case "o": setMarkOut(player.masterNs); break;
        case "m": addPoint(); break;
        case "enter": if (markIn != null && markOut != null) addInterval(); break;
        case "delete": case "backspace":
          if (selectedAnn) { e.preventDefault(); void deleteAnnotation(selectedAnn).catch(e => notify(e.message,"error")); }
          break;
        case "escape": setSelectedAnn(null); setMarkIn(null); setMarkOut(null); break;
        default:
          if (e.key === "[" && markIn != null && markOut != null) player.setLoop([markIn, markOut]);
          if (e.key === "]") player.setLoop(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [player, addPoint, addInterval, deleteAnnotation, selectedAnn, markIn, markOut, rangeNs, undo, redo, jumpChapter, notify]);

  useEffect(() => {
    for (const el of document.querySelectorAll<HTMLAudioElement>("audio")) el.volume = volume;
  }, [volume, detail]);

  /* ---------------- linked view transforms ---------------- */
  const setBothViews = useCallback(
    (which: "left" | "right", t: ViewTransform) => {
      if (which === "left") { setLeftView(t); if (linkViews) setRightView(t); }
      else { setRightView(t); if (linkViews) setLeftView(t); }
    },
    [linkViews],
  );

  if (error) {
    return <EmptyState title="Could not open this session" action={<button onClick={onBack}>Back to sessions</button>}>{error}</EmptyState>;
  }
  if (!detail) {
    return <div className="empty"><Spinner size={22} /></div>;
  }

  const leftStream = detail.streams.find((s) => s.stream_id === "camera.left");
  const rightStream = detail.streams.find((s) => s.stream_id === "camera.right");
  const phoneTest = detail.manifest?.capture_source === "phone_test";
  const calibration = {
    profile: detail.manifest?.device?.calibration_profile ?? null,
    is_measured: Boolean(detail.manifest?.device?.calibration_is_measured),
    status: detail.manifest?.device?.calibration_status ?? "unavailable",
    estimate: detail.manifest?.device?.calibration_estimate,
  };
  const audioStream = detail.streams.find((s) => s.kind === "audio") ?? null;

  // Analyses are sampled, so "the result at this instant" is the nearest
  // analysed sample — and it is only shown while it is recent enough to still
  // describe what is on screen. Holding a stale skeleton over a moved hand is
  // exactly the kind of quiet lie this tool is meant not to tell.
  const maxSampleAgeNs = 1_000_000_000;
  const nearestSample = (times: number[] | undefined) => {
    if (!times || times.length === 0) return null;
    const i = nearestIndex(times, player.masterNs);
    return Math.abs(times[i] - player.masterNs) <= maxSampleAgeNs ? i : null;
  };
  const nearestAnalysed = (times: number[]) => times[nearestIndex(times, player.masterNs)];
  const handSampleIndex = nearestSample(handFrames?.session_ns);
  const handSample = handSampleIndex != null ? handFrames!.samples[handSampleIndex] : null;
  const sourceSize = (role: "left" | "right"): [number, number] | null => {
    const stream = detail.streams.find((s) => s.stream_id === `camera.${role}`);
    const probe = stream?.probe?.streams?.find((x: any) => x.type === "video");
    return probe ? [probe.width, probe.height] : null;
  };

  const objectIndex = nearestIndex(objectTimes, player.masterNs);
  const objectSample = objectFrames[objectIndex];
  const gazeIndex = nearestIndex(gazeTimes, player.masterNs);
  const gazeSample = gazeSamples[gazeIndex];
  const makeOverlay = (eye: "left" | "right") => {
    return (ctx: CanvasRenderingContext2D, width: number, height: number, sourcePixelsPerCssPixel: number) => {
      const faceSample = facesByEye[eye][nearestIndex(faceTimes[eye], player.masterNs)];
      if (overlays["analysis.faces"] && faceSample && Math.abs(faceSample.session_ns-player.masterNs)<75_000_000) {
        ctx.strokeStyle="#c7a5ff";ctx.fillStyle="#c7a5ff";ctx.lineWidth=Math.max(2,width/400);ctx.font=`${Math.max(12,width/60)}px sans-serif`;
        for (const face of faceSample.faces) {
          const [x1,y1,x2,y2]=face.box;ctx.strokeRect(x1*width,y1*height,(x2-x1)*width,(y2-y1)*height);
          ctx.fillText(`Face ${Math.round(face.confidence*100)}%`,x1*width,Math.max(16,y1*height-5));
        }
      }
      if (overlays["analysis.hands"] && handFrames && handSample) {
        const accepted = new Set(handSample.arms3d.filter(a => a.accepted && a.name).map(a => a.name as string));
        drawHands(ctx, handSample.hands[eye], handFrames.skeleton.hand_connections ?? []);
        drawArms(ctx, handSample.arms[eye], accepted, handFrames.skeleton.arm_connections ?? [], {showRejected: showRejectedArms});
      }
      if (eye === "left" && overlays["analysis.objects"] && objectSample && Math.abs(objectSample.session_ns - player.masterNs) <= 250_000_000) {
        const font = objectCaptionFont(width, sourcePixelsPerCssPixel);
        ctx.font = `600 ${font}px system-ui, sans-serif`;
        ctx.lineWidth = Math.max(4,width/180);
        const occupied:LabelRect[]=[];
        const sortedObjects=objectSample.objects
          .filter(object => objectVisibility.selection === null || objectVisibility.selection.has(object.label))
          .sort((a,b)=>b.confidence-a.confidence);
        // Draw geometry first so a later box cannot paint over an earlier caption.
        for (const obj of sortedObjects) {
          const [x1,y1,x2,y2] = obj.box;
          const x=x1*width,y=y1*height,w=(x2-x1)*width,h=(y2-y1)*height;
          ctx.fillStyle="rgba(40,255,187,.08)";ctx.fillRect(x,y,w,h);
          ctx.strokeStyle="#43ffbc";ctx.setLineDash(obj.evidence === "interpolated" ? [width/120,width/180] : []);ctx.strokeRect(x,y,w,h);
          ctx.setLineDash([]);
        }
        for (const obj of sortedObjects) {
          const [x1,y1]=obj.box,x=x1*width,y=y1*height;
          const text=obj.evidence === "interpolated" ? obj.label : `${obj.label} · ${Math.round(obj.confidence*100)}%`;
          const pad=font*.25,tw=Math.min(width,ctx.measureText(text).width+pad*2),th=font*1.35;
          const placed=placeLabel({x,y},{width:tw,height:th},{width,height},occupied);
          if (!placed) continue;
          occupied.push(placed);
          const {x:tx,y:ty}=placed;
          if (Math.abs(ty-y)>font*1.5 || Math.abs(tx-x)>font) {
            ctx.strokeStyle="#43ffbca0";ctx.lineWidth=width/500;ctx.beginPath();ctx.moveTo(x,y);ctx.lineTo(tx+tw/2,ty+th/2);ctx.stroke();
          }
          ctx.fillStyle="rgba(3,22,19,.94)";ctx.fillRect(tx,ty,tw,th);
          ctx.fillStyle="#aaffdf";ctx.fillText(text,tx+pad,ty+font,tw-pad*2);
        }

      }
      if (eye === "left" && overlays["stream.gaze"] && gazeSample?.valid && gazeSample.x != null && gazeSample.y != null && Math.abs(gazeSample.session_ns-player.masterNs) <= 100_000_000) {
        // Paint gaze last, above depth and every other annotation. Keep the centre clear.
        const unit = sourcePixelsPerCssPixel;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
        ctx.setLineDash([]);
        // Offset in image coordinates so it follows zoom, pan and resizing.
        // Let the canvas clip off-image positions instead of pinning them to an edge.
        ctx.beginPath(); ctx.arc((gazeSample.x + gazeOffset.offset.x / 100)*width,
          (gazeSample.y + gazeOffset.offset.y / 100)*height,11*unit,0,2*Math.PI);
        ctx.strokeStyle = "rgba(20, 5, 5, .75)"; ctx.lineWidth = 6*unit; ctx.stroke();
        ctx.strokeStyle = "#ff3b30"; ctx.lineWidth = 4*unit; ctx.stroke();
        ctx.restore();
      }
    };
  };

  const leftFrame = player.frameAt("camera.left");
  const rightFrame = player.frameAt("camera.right");
  const previewUrl = (stream: typeof leftStream, index: number | undefined) => stream?.frame_preview_url && index != null ? `${stream.frame_preview_url}&frame_index=${index}` : undefined;
  const leftPreview = previewUrl(leftStream, leftFrame?.index);
  const rightPreview = previewUrl(rightStream, rightFrame?.index);
  const nearbyPreviews = (stream: typeof leftStream, index: number | undefined) => index == null ? [] : [index + 1, index - 1]
    .filter(i => i >= 0 && i < (frames[stream?.stream_id ?? ""]?.session_ns.length ?? 0))
    .map(i => previewUrl(stream, i)!);
  const pairDeltaMs = leftFrame && rightFrame ? (rightFrame.ns - leftFrame.ns) / 1e6 : null;
  const overlayCapabilities = (info?.capabilities ?? []).filter(
    (c) => (c.id.startsWith("overlay.") || c.id.startsWith("analysis.") || c.id.startsWith("stream."))
      && (c.id !== "stream.gaze" || gazeSamples.length > 0)
      && !["analysis.depth", "analysis.pointcloud", "overlay.rectified_preview"].includes(c.id)
      && (!phoneTest || !["analysis.depth", "analysis.pointcloud", "overlay.rectified_preview"].includes(c.id)),
  );
  const capabilityResult = (id: string): boolean | null => {
    const pluginId = ({"analysis.dense_depth":"depth.relative", "analysis.faces":"faces.mediapipe",
      "analysis.hands":"hands.mediapipe", "analysis.objects":"objects.yolox"} as Record<string,string>)[id];
    if (pluginId) {
      const plugin = plugins.find(p => p.id === pluginId);
      if (plugin) return plugin.has_result;
      // Written when processing finishes; available with the first session response.
      if (detail?.manifest?.derived?.some((r: any) => r.plugin?.id === pluginId)) return true;
      return null; // Unknown is not "Not run".
    }
    if (id === "stream.gaze") return gazeSamples.length > 0;
    return null;
  };
  const toggleOverlay = (id: string) => {
    setOverlays((prev) => ({ ...prev,
      [id]: !prev[id] }));
    if (capabilityResult(id) === false) setTab("analysis");
  };
  const showDepth = relativeDepth;

  return (
    <div className="review-workspace" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
      {/* header */}
      <div className="row review-heading" style={{ padding: "4px 10px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <button className="ghost" onClick={onBack}>← Library</button>
        <span className="mono" style={{ fontWeight: 600 }}>{detail.session.name}</span>
        {phoneTest ? <Badge kind="info">Phone test · duplicated camera</Badge> : <>
        <Badge kind={detail.session.sync_quality}><StateDot state={detail.session.sync_quality} /> sync {detail.session.sync_quality}</Badge>
        {sync?.audio_check?.correction_applied ? <Badge kind="info" title="Software alignment from repeated audio matches; not hardware synchronization">audio aligned · {(-(sync.audio_check.offset_ms ?? 0)).toFixed(1)} ms</Badge> : sync && !sync.offset_verified ? <Badge kind="warning" title={sync.notes[0]}>offset unverified</Badge> : null}
        <Badge
          kind={
            calibration.is_measured
              ? "good"
              : calibration.status === "estimated_from_recording"
                ? "info"
                : "warning"
          }
          title={
            calibration.is_measured
              ? `Measured calibration ${calibration.profile}`
              : calibration.status === "estimated_from_recording"
                ? "The cameras' relative pose was measured from this recording; the baseline length and the intrinsics are still assumed, so distances are only as good as the configured baseline."
                : `${calibration.profile ?? "No profile"} assumes the cameras are parallel. They usually are not — run "estimate from recording" in the analysis tab.`
          }
        >
          {calibration.is_measured
            ? `calibration ${calibration.profile}`
            : calibration.status === "estimated_from_recording"
              ? "geometry estimated · scale assumed"
              : "approximate / uncalibrated"}
        </Badge>
        </>}
        <span className="spacer" />
        <button className="ghost" onClick={async () => {
          try { await navigator.clipboard.writeText(location.href); notify("Review link copied"); }
          catch { notify("Copy the address from your browser to share this review."); }
        }}>Copy link</button>
        <button className="ghost danger" onClick={async () => {
          if (!confirm("Remove this capture and its managed workspace files? External source files are retained.")) return;
          try { await api.deleteSession(sessionId, true); onBack(); } catch (e) { notify((e as Error).message, "error"); }
        }}>Remove</button>
        <span className="tiny muted mono truncate review-source" style={{ maxWidth: 380 }} title={detail.session.source_root}>
          {detail.session.source_root}
        </span>
      </div>

      <SplitPane
        direction="horizontal"
        initial={0.74}
        min={0.4}
        max={0.88}
        storageKey="pads.review.hsplit"
        first={
          <div className="review-player" style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, padding: 10, gap: 8 }}>
            <div className="row review-overlay-toolbar">
              <OverlayBar
                capabilities={overlayCapabilities}
                active={overlays}
                onToggle={toggleOverlay}
                hasResult={capabilityResult}
                settings={{
                  "analysis.objects": <ObjectVisibilitySettings labels={objectVisibility.labels}
                    selection={objectVisibility.selection} onChange={objectVisibility.update}
                    saved={objectVisibility.saved} enabled={!!overlays["analysis.objects"]} />,
                  "stream.gaze": <GazeOffsetSettings offset={gazeOffset.offset} onChange={gazeOffset.update} saved={gazeOffset.saved} />,
                  "analysis.dense_depth": <>
                    <label className="overlay-opacity">
                      <span>Opacity</span><output>{Math.round(depthOpacity * 100)}%</output>
                      <input type="range" min={0} max={1} step={0.05} value={depthOpacity}
                        aria-label="AI depth opacity" onChange={event => setDepthOpacity(Number(event.target.value))} />
                    </label>
                    <p className="tiny muted">Full-frame AI estimate · near / far</p>
                    <span className="tiny mono muted">{denseDepth
                      ? `${denseDepth.analysed} frames · ${denseDepth.every_frame ? "every frame" : "sampled preview"}`
                      : "Depth results are not available yet."}</span>
                  </>,
                  ...(handFrames ? {"analysis.hands": <>
                    <p className="tiny muted">{handSample
                      ? `${handSample.hands.left.length + handSample.hands.right.length} hand(s) · ${handSample.arms3d.filter(a => a.accepted).length} arm joint(s) accepted`
                      : "No analysed frame within 1 s of the playhead"}</p>
                    {!handSample && handFrames.session_ns.length ? (
                      <button className="ghost" onClick={() => player.seek(nearestAnalysed(handFrames.session_ns))}>
                        Jump to the nearest analysed frame
                      </button>
                    ) : null}
                    <button className={showRejectedArms ? "active" : "ghost"} aria-pressed={showRejectedArms}
                      onClick={() => setShowRejectedArms(v => !v)}
                      title="Draw the arm joints the two eyes disagreed about, as hollow markers">
                      Show rejected arm joints
                    </button>
                  </>} : {}),
                }}
              />
              {overlays["stream.gaze"] && gazeSamples.some(g => g.calibrated === false) &&
                <span className="tiny muted" title="Projection uses assumed field of view and target depth; see capture metadata.">Gaze estimate · uncalibrated</span>}
            </div>

            {/* Audio is its own stream, not a track inside a proxy (ADR 0006), so it
                gets its own element driven by the same master clock. */}
            {sources.filter((s) => s.kind === "audio").map((s) => (
              <audio
                key={s.key}
                ref={player.register(s.key)}
                src={s.url}
                preload="auto"
                muted={muted}
                onLoadedMetadata={() => player.onLoadedMetadata(s.key)}
                style={{ display: "none" }}
              />
            ))}

            <div className="row review-videos" style={{ flex: 1, minHeight: 0, gap: 8, alignItems: "stretch" }}>
              <VideoPanel
                label="Left eye"
                colour="var(--left-eye)"
                url={sources.find((s) => s.key === "camera.left")?.url ?? null}
                register={player.register("camera.left")}
                onLoadedMetadata={() => player.onLoadedMetadata("camera.left")}
                framePreview={(!player.playing || player.preparing) && leftPreview ? {url:leftPreview, nearby:nearbyPreviews(leftStream,leftFrame?.index), onReady:url => {readyPreviews.current.left = url;}} : undefined}
                transform={leftView}
                onTransform={(t) => setBothViews("left", t)}
                frameInfo={leftFrame ? { index: leftFrame.index, deltaMs: null } : null}
                badges={leftStream ? <span className="tiny muted mono">{leftStream.probe?.streams?.[0]?.width}×{leftStream.probe?.streams?.[0]?.height}</span> : null}
                sourceSize={sourceSize("left")}
                drawOverlay={makeOverlay("left")}
                layers={
                  showDepth && depthVideo ? (
                    <DepthLayer
                      key={depthVideo.url}
                      url={depthVideo.url}
                      targetTime={player.toElement("depth.overlay", player.masterNs)}
                      paused={!player.playing || player.preparing}
                      frameUrl={denseDepth?.frame_url ? `${denseDepth.frame_url}&session_ns=${Math.round(leftFrame?.ns ?? player.masterNs)}` : undefined}
                      referenceVideo={() => player.getElement("camera.left") as HTMLVideoElement | null}
                      referenceTime={player.toElement("camera.left", player.masterNs)}
                      referencePreview={leftPreview ? () => readyPreviews.current.left === leftPreview : undefined}
                      nearbyFrames={denseDepth?.frame_url && leftFrame ? [leftFrame.index+1,leftFrame.index-1].filter(i=>i>=0 && i<(frames['camera.left']?.session_ns.length ?? 0)).map(i=>`${denseDepth.frame_url}&session_ns=${Math.round(frames['camera.left'].session_ns[i])}`) : []}
                      register={player.register("depth.overlay")}
                      onLoadedMetadata={() => player.onLoadedMetadata("depth.overlay")}
                      opacity={depthOpacity}
                      relative={relativeDepth}
                      nearM={depthVideo.near_m}
                      farM={depthVideo.far_m}
                    />
                  ) : null
                }
                footer={showDepth ? "AI depth · near → far · relative scale" : null}
              />
              <VideoPanel
                label="Right eye"
                colour="var(--right-eye)"
                url={sources.find((s) => s.key === "camera.right")?.url ?? null}
                register={player.register("camera.right")}
                onLoadedMetadata={() => player.onLoadedMetadata("camera.right")}
                framePreview={(!player.playing || player.preparing) && rightPreview ? {url:rightPreview, nearby:nearbyPreviews(rightStream,rightFrame?.index), onReady:url => {readyPreviews.current.right = url;}} : undefined}
                transform={rightView}
                onTransform={(t) => setBothViews("right", t)}
                frameInfo={rightFrame ? { index: rightFrame.index, deltaMs: pairDeltaMs } : null}
                badges={rightStream ? <span className="tiny muted mono">{rightStream.probe?.streams?.[0]?.width}×{rightStream.probe?.streams?.[0]?.height}</span> : null}
                sourceSize={sourceSize("right")}
                drawOverlay={makeOverlay("right")}
              />
            </div>

            <FrameLabels annotations={annotations} time={player.masterNs} selectedId={selectedAnn}
              onPause={player.pause} onSelect={id=>{setSelectedAnn(id);setTab("annotations");}} onUpdate={updateAnnotation}
              onAdd={async (label, level) => {
                const active = currentActivity(annotations,player.masterNs,level);
                const start = markIn ?? active?.start_ns ?? player.masterNs;
                const end = markOut ?? active?.end_ns ?? Math.min(rangeNs[1],start+5*NS);
                if(end<=start)throw new Error("Mark an interval before adding a chapter");
                await createAnnotation({kind:"interval",start_ns:start,end_ns:end,label,source:"manual",review_status:"accepted",
                  payload:{type:"activity",level}});
              }} />
            {/* Native touch seeking complements the detailed desktop timeline. */}
            <input className="mobile-seek" type="range" aria-label="Seek video" min={rangeNs[0]} max={Math.max(rangeNs[0] + 1, rangeNs[1])}
              step={Math.max(1, (rangeNs[1] - rangeNs[0]) / 1000)} value={Math.max(rangeNs[0], Math.min(rangeNs[1], player.masterNs))}
              onChange={e => player.seek(Number(e.target.value))} />
            {/* transport */}
            <div className="review-transport">
              <div className="transport-line">
                <div className="transport-group">
                  <button onClick={() => player.seek(rangeNs[0])} title="Start (Home)">⏮</button>
                  <button onClick={() => player.stepFrames(-1)} disabled={!player.canStep} title="Previous frame (←)">◀|</button>
                  <button className="primary transport-play" onClick={player.toggle} title="Play/pause (Space)">{player.playing ? "Pause" : "Play"}</button>
                  <button onClick={() => player.stepFrames(1)} disabled={!player.canStep} title="Next frame (→)">|▶</button>
                  <select aria-label="Playback speed" value={player.rate} onChange={e=>player.setRate(Number(e.target.value))}>
                    {[0.25,0.5,1,1.5,2,4].map(r=><option key={r} value={r}>{r}×</option>)}
                  </select>
                </div>
                <span className="transport-time mono">{formatTimecode(player.masterNs)}</span>
                <div className="transport-frames mono tiny" aria-label="Frame alignment">
                  <span>L#{leftFrame?.index ?? "—"}</span><span>R#{rightFrame?.index ?? "—"}</span>
                  <span>Δ {pairDeltaMs == null ? "—" : `${pairDeltaMs>=0?"+":""}${pairDeltaMs.toFixed(1)}`} ms</span>
                </div>
                <div className="transport-group transport-audio">
                  <button className={muted ? "" : "active"} onClick={()=>setMuted(v=>!v)} disabled={!audioStream}>{muted || !audioStream ? "Muted" : "Audio"}</button>
                  <input aria-label="Volume" type="range" min={0} max={1} step={.02} value={volume} onChange={e=>setVolume(Number(e.target.value))} disabled={!audioStream || muted}/>
                </div>
                <button className="transport-chapter" onClick={()=>jumpChapter(-1)} disabled={!adjacentChapter(annotations,player.masterNs,-1)} title={`Previous label (${isMac()?"⌘":"Ctrl+"}←)`}>⇤</button>
                <button className="transport-chapter" onClick={()=>jumpChapter(1)} disabled={!adjacentChapter(annotations,player.masterNs,1)} title={`Next label (${isMac()?"⌘":"Ctrl+"}→)`}>⇥</button>
                <button className="transport-undo" onClick={()=>void undo()} disabled={!history.canUndo && !history.pending} title="Undo edit">↶</button>
                <details className="review-options transport-more"><summary>More</summary><div className="review-options-menu">
                <button className={linkViews ? "active" : ""} aria-pressed={linkViews}
                  onClick={() => setLinkViews(v => !v)} title="Keep both video views at the same zoom and pan">
                  Link views
                </button>
              <div className="transport-line transport-editing">
                <button onClick={()=>jumpChapter(-1)} disabled={!adjacentChapter(annotations,player.masterNs,-1)} title={`${isMac()?"⌘":"Ctrl+"}←`}>← Label</button>
                <button onClick={()=>jumpChapter(1)} disabled={!adjacentChapter(annotations,player.masterNs,1)} title={`${isMac()?"⌘":"Ctrl+"}→`}>Label →</button>
                <button onClick={()=>void undo()} disabled={!history.canUndo && !history.pending} title={`${isMac()?"⌘":"Ctrl+"}Z`}>Undo</button>
                <button onClick={()=>void redo()} disabled={!history.canRedo && !history.pending}>Redo</button>
                <span className="transport-marks mono tiny">in {markIn == null ? "—" : formatTimecode(markIn)} / out {markOut == null ? "—" : formatTimecode(markOut)}</span>
                <button disabled={markIn==null || markOut==null || markOut<=markIn} onClick={()=>player.setLoop([markIn!,markOut!])}>Loop</button>
                <button disabled={markIn==null || markOut==null || markOut<=markIn} onClick={()=>addInterval()}>Add interval</button>
              </div>
              <div className="transport-diagnostics mono tiny muted" aria-label="Playback diagnostics">
                <span>{player.diagnostics?.fps.toFixed(1) ?? "—"} fps</span>
                <span>{player.diagnostics?.dropped ?? 0}/{player.diagnostics?.total ?? 0} dropped</span>
                <span>R {player.followers.find(f=>f.key==="camera.right")?.driftMs.toFixed(0) ?? "—"} ms</span>
                <span>Audio {player.followers.find(f=>f.key===audioStream?.stream_id)?.driftMs.toFixed(0) ?? "—"} ms</span>
                <span>Depth {player.followers.find(f=>f.key==="depth.overlay")?.driftMs.toFixed(0) ?? "—"} ms</span>
                <span className="transport-status">{player.blockedReason ? "Press Play again" : player.preparing ? "Preparing playback…" : history.pending ? "Saving…" : ""}</span>
              </div>
                </div></details>
              </div>
            </div>
            <div className="panel review-timeline" style={{ padding: 0 }}>
              <Timeline
                rangeNs={rangeNs}
                viewNs={viewNs}
                setViewNs={setViewNs}
                masterNs={player.masterNs}
                onSeek={(ns) => player.seek(ns)}
                data={{
                  leftFrames: frames["camera.left"]?.session_ns ?? [],
                  rightFrames: frames["camera.right"]?.session_ns ?? [],
                  waveform,
                  syncSeries,
                  maxPairDeltaMs: sync?.max_pair_delta_ms ?? 16.7,
                }}
                annotations={annotations.filter(isActivityLabel)}
                selectedId={selectedAnn}
                onSelect={setSelectedAnn}
                onCreateInterval={(a, b) => addInterval(a, b)}
                onUpdateInterval={async (id, patch) => {try {await updateAnnotation(id,patch);} catch(e) {notify((e as Error).message,"error");}}}
                loop={player.loop}
                setLoop={player.setLoop}
              />
            </div>
            <div className="tiny muted desktop-timeline-hint">
              Scroll to zoom · Shift+scroll to pan · drag a chapter to move it or its edges to resize · {isMac() ? "Option" : "Alt"}+drag to add
            </div>
          </div>
        }
        second={
          <div className="panel review-inspector" style={{ flex: 1, margin: "10px 10px 10px 0", minWidth: 0 }}>
            <div className="panel-head" style={{ gap: 2, padding: 0, overflowX: "auto" }}>
              {(["overview", "annotations", "analysis", "sync", "export", "streams", "shortcuts"] as Tab[]).map((t) => (
                <button key={t} className={tab === t ? "active" : "ghost"} style={{ borderRadius: 0, padding: "8px 11px" }} onClick={() => setTab(t)}>
                  {t}
                </button>
              ))}
            </div>
            <div className="panel-body" style={{ display: "flex", flexDirection: "column" }}>
              {tab === "overview" ? (
                <CaptureOverview detail={detail} plugins={plugins} annotations={annotations} gaze={gazeSamples} onAnnotations={() => setTab("annotations")} onAnalyze={() => setTab("analysis")} />
              ) : tab === "annotations" ? (
                <AnnotationInspector
                  annotations={annotations}
                  ontology={ontology}
                  selectedId={selectedAnn}
                  onSelect={setSelectedAnn}
                  onUpdate={updateAnnotation}
                  onDelete={(id) => void deleteAnnotation(id).catch(e => notify(e.message,"error"))}
                  onSeek={(ns) => player.seek(ns)}
                  masterNs={player.masterNs}
                  onAddPoint={addPoint}
                  onAddInterval={() => addInterval()}
                  exportUrl={api.exportAnnotationsUrl(sessionId)}
                  pendingRange={markIn != null && markOut != null ? [markIn, markOut] : null}
                />
              ) : tab === "analysis" ? (
                <AnalysisPanel
                  sessionId={sessionId}
                  plugins={plugins}
                  jobs={jobs.filter(j=>j.session_id===sessionId)}
                  hands={handFrames}
                  phoneTest={phoneTest}
                  calibration={calibration}
                  currentSample={{ hands: handSampleIndex }}
                  onRefresh={() => void loadAnalysis()}
                />
              ) : tab === "sync" ? (
                <SyncInspector
                  sessionId={sessionId}
                  sync={sync}
                  mappings={mappings}
                  series={syncSeries}
                  frameIntervalNs={sync?.frame_interval_ns ?? null}
                  onChanged={() => {
                    void loadSync(); void api.session(sessionId).then(setDetail); void loadAnalysis();
                    void Promise.all([api.frames(sessionId,"left"), api.frames(sessionId,"right")])
                      .then(([left,right]) => setFrames({"camera.left":left,"camera.right":right}));
                  }}
                />
              ) : tab === "export" ? (
                <ExportPanel sessionId={sessionId} jobs={jobs} />
              ) : tab === "streams" ? (
                <StreamsTab detail={detail} frames={frames} />
              ) : (
                <ShortcutsTab canStep={player.canStep} hasChapters={annotations.some(isActivityLabel)} />
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}

function StreamsTab({ detail, frames }: { detail: SessionDetail; frames: Record<string, FrameIndexResponse> }) {
  return (
    <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
      {detail.streams.map((s) => {
        const probe = s.probe?.streams?.find((x: any) => x.type === s.kind) ?? s.probe?.streams?.[0];
        const idx = frames[s.stream_id];
        return (
          <div key={s.stream_id}>
            <div className="row" style={{ marginBottom: 5 }}>
              <span className="mono" style={{ fontWeight: 600 }}>{s.stream_id}</span>
              <Badge kind={s.source_present === false ? "invalid" : "neutral"}>
                {s.source_present === false ? "source missing" : s.kind}
              </Badge>
            </div>
            <KeyValue
              rows={[
                ["Source", <span title={s.source_path}>{s.source_path.split("/").pop()}</span>],
                ["Size", formatBytes(s.probe?.size_bytes)],
                ["Codec", probe ? `${probe.codec} ${probe.width ? `${probe.width}×${probe.height}` : ""} ${probe.sample_rate ? `${probe.sample_rate} Hz ${probe.channels}ch` : ""}` : "—"],
                ["Time base", probe?.time_base ?? "—"],
                ["First PTS", probe?.start_ns != null ? `${(probe.start_ns / 1e6).toFixed(2)} ms` : "—"],
                ["Frames", idx ? `${idx.count}` : s.stats?.frames ?? "—"],
                ["Mean rate", idx?.mean_fps ? `${idx.mean_fps.toFixed(3)} fps (measured)` : "—"],
                ["Nominal rate", probe?.avg_rate ? `${probe.avg_rate.toFixed(3)} fps (container)` : "—"],
                ["Dropped-frame gaps", s.stats?.gap_count ?? "—"],
                ["Time source", s.mapping?.source ?? "—"],
                ["Raw link", s.stats?.raw_link_mode ?? "—"],
              ]}
            />
          </div>
        );
      })}
      <div className="tiny muted" style={{ lineHeight: 1.5 }}>
        Frame timestamps come from the container's packet index, not from the nominal frame rate —
        these recordings are variable frame rate and the two disagree by more than a frame within seconds.
      </div>
    </div>
  );
}

function ShortcutsTab({canStep, hasChapters}: {canStep:boolean;hasChapters:boolean}) {
  const mod = isMac() ? "⌘" : "Ctrl + ";
  const alt = isMac() ? "Option" : "Alt";
  const rows: [string, string][] = [
    ["Space", "Play / pause"],
    ["J / K / L", "Rewind 1 s · pause · faster"],
    ...(canStep ? [["← / →", "Step one frame"], ["Shift + ← / →", "Step ten frames"]] as [string,string][] : []),
    ...(hasChapters ? [[`${mod}← / ${mod}→`, "Previous / next chapter"]] as [string,string][] : []),
    ["Home / End", "Start / end of session"],
    ["I / O", "Mark in / out"],
    ["Enter", "Create an interval from in/out"],
    ["M", "Add a point annotation at the playhead"],
    [`${alt} + drag`, "Mark an interval directly on the timeline"],
    ["[ / ]", "Loop the marked range / clear the loop"],
    ["Delete", "Delete the selected annotation"],
    [isMac() ? "⌘Z / ⇧⌘Z" : "Ctrl+Z / Ctrl+Y", "Undo / redo saved edit"],
    ["Drag chapter / edges", "Move / resize interval"],
    ["Esc", "Clear selection and marks"],
  ];
  return (
    <div style={{ padding: 12 }}>
      <p className="tiny muted">{isMac() ? "macOS" : "Windows / Linux"} · Text fields keep native undo while typing.</p>
      <table className="data">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ width: 130 }}><kbd>{k}</kbd></td>
              <td className="tiny">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
